import * as THREE from './three-min.js'

import { OrbitControls } from './OrbitControls.js'
import { GLTFLoader } from './GLTFLoader.js'
import { RGBELoader } from './RGBELoader.js'
import { DRACOLoader } from './DRACOLoader.js'
import { FontLoader } from './FontLoader.js'
import { TextGeometry } from './TextGeometry.js'

const renderScene = ({
                       customizerShadowWrapper,
                       previewShadowWrapper,
                       modelUrl,
                       assetsUrl,
                       colourTexture,
                       mraTexture,
                       normalTexture,
                       envMapParam,
                       fontColor,
                       fullscreen,
                     }) => {
  THREE.ColorManagement.enabled = true
  const cursor =
    previewShadowWrapper.shadowRoot.querySelector('div#custom-cursor')
  // unique to each store...replace if new store gets added
  const ShopifyAssetsUrl = `${assetsUrl
    .split('/')
    .filter((e, i) => i !== assetsUrl.split('/').length - 1)
    .join('/')}/`

  // Scene
  const container = previewShadowWrapper.shadowRoot.querySelector('div#app')
  const scene = new THREE.Scene()
  scene.background = new THREE.Color('#000000')

  // Camera
  const camera = new THREE.PerspectiveCamera(
    75,
    container.offsetWidth / container.offsetHeight,
    0.01,
    20,
  )

  const fontColorHex = parseInt ( fontColor.replace("#","0x"), 16 );

  const isMobile = () => {
    return window.innerWidth < 768
  }

  let z = 0.15
  if (isMobile()) {
    z = fullscreen ? 0.14 : 0.11
  }
  let cameraTarget = new THREE.Vector3(0, 0, z)
  camera.position.copy(cameraTarget)
  camera.rotation.x = 0

  // Renderer
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    toneMapping: THREE.ACESFilmicToneMapping,
    outputColorSpace: THREE.SRGBColorSpace,
    shadowMap: {
      enabled: false,
    },
    lookAt: (0, 0, 0),
  })
  renderer.setPixelRatio(window.devicePixelRatio)

  // Controls
  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableZoom = false
  controls.enableDamping = true
  controls.dampingFactor = 0.1
  controls.maxPolarAngle = Math.PI / 1.5
  controls.minPolarAngle = Math.PI / 6
  controls.autoRotate = true
  controls.autoRotateSpeed = 1
  controls.update()

  let orbitTarget

  // Resize
  const handleResize = () => {
    camera.aspect = container.offsetWidth / container.offsetHeight
    camera.updateProjectionMatrix()

    renderer.setSize(container.offsetWidth, container.offsetHeight)
  }

  handleResize()

  window.addEventListener('resize', handleResize)
  // window.visualViewport.addEventListener('resize', handleResize);

  // Camera Animation
  const lockCamera = () => {
    orbitTarget = new THREE.Vector3(
      cameraTarget.x,
      cameraTarget.y,
      -cameraTarget.z,
    )
    controls.enabled = false
  }

  const unlockCamera = () => {
    orbitTarget = null
    controls.enabled = true
  }

  // Custom Cursor

  const moveCustomCursor = (e) => {
    const posX = e.pageX
    const posY = e.pageY

    cursor.style.top = posY + 'px'
    cursor.style.left = posX + 'px'

    const windowX = posX / container.offsetWidth
    const windowY = posY / container.offsetHeight

    const threshold = 0.3

    threshold < windowX < 1 - threshold

    if (
      threshold > windowX ||
      windowX > 1 - threshold ||
      threshold > windowY ||
      windowY > 1 - threshold
    ) {
      cursor.classList.add('hidden')
    } else {
      cursor.classList.remove('hidden')
    }
  }

  const handleMouseDown = (e) => {
    cursor.classList.add('clicked')
    container.style.cursor = 'grabbing'
  }

  const handleMouseUp = (e) => {
    cursor.classList.remove('clicked')
    container.style.cursor = 'grab'
  }

  window.addEventListener('mousemove', moveCustomCursor)
  window.addEventListener('mousedown', handleMouseDown)
  window.addEventListener('mouseup', handleMouseUp)

  // Loading Manager
  const preloader =
    previewShadowWrapper.shadowRoot.querySelector('div#preloader')
  const progress = previewShadowWrapper.shadowRoot.querySelector('div#progress')

  const loadingManager = new THREE.LoadingManager()

  loadingManager.onProgress = function(url, itemsLoaded, itemsTotal) {
    const progressPercentage = itemsLoaded / itemsTotal
    progress.style.width = progressPercentage * 100 + '%'
  }
  loadingManager.onLoad = function() {
    preloader.classList.add('hidden')
  }

  // Loaders
  const loader = new GLTFLoader(loadingManager)
  const textureLoader = new THREE.TextureLoader(loadingManager)
  const dracoLoader = new DRACOLoader(loadingManager)
  dracoLoader.setDecoderPath(ShopifyAssetsUrl)
  dracoLoader.preload()
  loader.setDRACOLoader(dracoLoader)
  const hdrLoader = new RGBELoader(loadingManager)

  // Load Model
  let model
  const sceneGroup = new THREE.Group()
  const textLine1Group = new THREE.Group()
  const textLine2Group = new THREE.Group()

  loader.load(
    modelUrl,
    (glb) => {
      model = glb.scene

      const meshes = model.children[0].children

      const bottle = meshes[0]
      const glass = meshes[1]

      bottle.geometry.computeVertexNormals(true)
      glass.geometry.computeVertexNormals(true)

      bottle.material = new THREE.MeshPhysicalMaterial({
        side: THREE.DoubleSide,
        roughness: 5,
        flatShading: false,
        normalScale: new THREE.Vector2(1, -1),
      })

      const loadTextures = () => {
        const colorMap = textureLoader.load(colourTexture, (texture) => {
          texture.flipY = false
          texture.colorSpace = THREE.SRGBColorSpace
        })

        const mraMap = textureLoader.load(mraTexture, (texture) => {
          texture.flipY = false
          texture.colorSpace = THREE.SRGBColorSpace
        })

        const normalMap = textureLoader.load(normalTexture, (texture) => {
          texture.flipY = false
          texture.colorSpace = THREE.SRGBColorSpace
        })

        const envMap = hdrLoader.load(envMapParam, (texture) => {
          texture.mapping = THREE.EquirectangularReflectionMapping
        })

        // Glass
        glass.material = new THREE.MeshPhysicalMaterial({
          roughness: 0.05,
          transmission: 0.999,
          metalness: 0,
          normalScale: new THREE.Vector2(0.05, 0.05),
        })

        bottle.material.map = colorMap

        bottle.material.roughnessMap = mraMap
        bottle.material.metalnessMap = mraMap

        bottle.material.normalMap = normalMap
        glass.material.normalMap = normalMap

        bottle.material.envMap = envMap
        glass.material.envMap = envMap
        bottle.material.envMapIntensity = 0.2
        // glass.material.envMapIntensity = 0.5

        bottle.material.needsUpdate = true
        glass.material.needsUpdate = true
      }

      loadTextures()

      model.position.y = -0.01
      sceneGroup.add(model)
    },
    undefined,
    function(error) {
      console.log({ sceneError: error })
    },
  )

  // Lights
  const spotLightTarget = new THREE.Object3D()
  spotLightTarget.position.set(-0.1, 0, 0)

  const spotLight = new THREE.SpotLight(0xffffff)
  spotLight.position.set(0.2, 0.2, 0.2)
  spotLight.target = spotLightTarget
  spotLight.intensity = 1.5
  spotLight.angle = Math.PI / 6
  spotLight.distance = 3

  const backLightTarget = new THREE.Object3D()
  backLightTarget.position.set(-0.025, 0, 0)

  const backLight = new THREE.SpotLight(0xffffff)
  backLight.position.set(-0.15, 0.25, -0.3)
  backLight.target = backLightTarget
  backLight.intensity = 0.75
  spotLight.distance = 1
  backLight.angle = Math.PI / 8

  // const spotHelper = new THREE.SpotLightHelper(backLight)
  // scene.add(spotHelper)

  scene.add(sceneGroup, spotLight, spotLightTarget, backLight, backLightTarget)
  // scene.add( sceneGroup );

  // Load Font
  const fontLoader = new FontLoader()

  let calibriFont, garamondFont, carattereFont
  let textMaterial
  let textMeshLine1, textMeshLine2
  let currentFont

  // Text Material
  textMaterial = new THREE.MeshLambertMaterial({
    color: fontColorHex,
    transparent: true
  })

  // Update Engraving
  const updateLine1 = ({ lock, value }) => {
    lock && lockCamera()
    updateText(value, 1, 0.006)
  }

  const updateLine2 = ({ lock, value }) => {
    lock && lockCamera()
    updateText(value, 2, 0.002)
  }

  // Live Text Input
  const engravingTextLine1 = customizerShadowWrapper?.shadowRoot?.querySelector(
    'input[name="bottle_engraving_line_1"]',
  )
  engravingTextLine1.addEventListener('input', (e) =>
    updateLine1({ lock: true, value: e.target.value }),
  )
  engravingTextLine1.addEventListener('blur', unlockCamera)

  const engravingTextLine2 = customizerShadowWrapper?.shadowRoot?.querySelector(
    'input[name="bottle_engraving_line_2"]',
  )
  engravingTextLine2.addEventListener('input', (e) =>
    updateLine2({ lock: true, value: e.target.value }),
  )
  engravingTextLine2.addEventListener('blur', unlockCamera)

  // Live Text Font
  const engravingFont = customizerShadowWrapper?.shadowRoot?.querySelector(
    'slot[name="font-style"]',
  )
  let activeElem = ''
  const updateActiveElem = () => {
    activeElem = engravingFont
      .assignedElements({ flatten: true })
      .find((item) => item?.classList.contains('active'))
  }
  updateActiveElem()
  engravingFont.addEventListener('click', () => {
    updateActiveElem()
    updateLine1({ lock: false, value: engravingTextLine1.value })
    updateLine2({ lock: false, value: engravingTextLine2.value })
  })

  const clamp = (min, preferred, max) => {
    return Math.min(Math.max(min, preferred), max)
  }
  const updateText = (text, line, positionY) => {
    let baseFontSize = 0.005
    let maxFontWidth = 100

    if (isMobile()) {
      maxFontWidth = 90
    }

    const fontMapper = {
      simple: 'calibri',
      block: 'garamond',
      script: 'carattere',
    }
    const textWidthElem = customizerShadowWrapper?.shadowRoot?.querySelector(
      'input[name="bottle_engraving_line_' + line + '"]',
    ).parentNode.querySelector('.line-char-width')

    const fontSizePreferred = (maxFontWidth / textWidthElem.offsetWidth) * baseFontSize
    const fontSize = clamp(0.002, fontSizePreferred, 0.005)
    positionY = line === 1 ? 0.009 : 0.004 - (fontSize / 2)

    let mesh
    currentFont = fontMapper[activeElem.dataset.style]

    if (line === 1 && textMeshLine1) {
      scene.remove(textMeshLine1)
    }

    if (line === 2 && textMeshLine2) {
      scene.remove(textMeshLine2)
    }

    const createTextGeometry = (font, mesh) => {
      const textGeometry = new TextGeometry(text, {
        font: font,
        size: fontSize,
        height: -0.0002,
        curveSegments: 14,
        // Too much computing necessary for bevel
        // bevelEnabled: true,
        // bevelThickness: 0.0001,
        // bevelSize: 0.00003,
        // bevelOffset: 0.00002,
      })

      textGeometry.center()
      textGeometry.rotateY(Math.PI)

      // Offset Vertices to Avoid Clipping
      const vertices = textGeometry.attributes.position.array
      for (let i = 0; i < textGeometry.attributes.position.count; i++) {
        vertices[i * 3 + 2] -= Math.abs(vertices[i * 3 + 0]) ** 1.55
      }
      textGeometry.attributes.position.needsUpdate = true

      if (line === 1) {
        while (textLine1Group.children.length > 0) {
          textLine1Group.remove(textLine1Group.children[0])
        }
        textMeshLine1 = new THREE.Mesh(textGeometry, textMaterial)
        mesh = textMeshLine1
      }

      if (line === 2) {
        while (textLine2Group.children.length > 0) {
          textLine2Group.remove(textLine2Group.children[0])
        }
        textMeshLine2 = new THREE.Mesh(textGeometry, textMaterial)
        mesh = textMeshLine2
      }

      mesh.position.y = positionY
      mesh.position.z = -0.0144
      line === 1 && textLine1Group.add(mesh)
      line === 2 && textLine2Group.add(mesh)

      line === 1 && scene.add(textLine1Group)
      line === 2 && scene.add(textLine2Group)
    }

    if (currentFont == 'garamond' && !garamondFont) {
      fontLoader.load(
        `${ShopifyAssetsUrl}Garamond_Regular.json`,
        function(font) {
          createTextGeometry(font, mesh)
          garamondFont = font
        },
      )
    }

    if (currentFont == 'calibri' && !calibriFont) {
      fontLoader.load(
        `${ShopifyAssetsUrl}Calibri_Regular.json`,
        function(font) {
          createTextGeometry(font, mesh)
          calibriFont = font
        },
      )
    }

    if (currentFont == 'carattere' && !carattereFont) {
      fontLoader.load(
        `${ShopifyAssetsUrl}Carattere_Regular.json`,
        function(font) {
          createTextGeometry(font, mesh)
          carattereFont = font
        },
      )
    }

    if (currentFont == 'garamond' && garamondFont) {
      createTextGeometry(garamondFont, mesh)
    }

    if (currentFont == 'calibri' && calibriFont) {
      createTextGeometry(calibriFont, mesh)
    }

    if (currentFont == 'carattere' && carattereFont) {
      createTextGeometry(carattereFont, mesh)
    }
  }

  engravingTextLine1?.value && updateText(engravingTextLine1.value, 1, 0.008)
  engravingTextLine2?.value && updateText(engravingTextLine2.value, 2, 0.001)

  // Animate
  function animate() {
    if (orbitTarget) {
      controls.object.position.lerp(orbitTarget, 0.1)
    }

    controls.update()
    renderer.render(scene, camera)

    requestAnimationFrame(animate)
  }

  animate()

  container.appendChild(renderer.domElement)
  return scene
}

const clearScene = ({ scene, previewShadowWrapper }) => {
  while (scene.children.length > 0) {
    scene.remove(scene.children[0])
  }
  const container = previewShadowWrapper.shadowRoot.querySelector('div#app')
  container.innerHTML = ''
}

export { renderScene, clearScene }
